import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./About.css";

const About = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const heroRef = useRef(null);
  const particlesRef = useRef([]);
  
  useEffect(() => {
    initParticleAnimation();
    initScrollAnimations();
    initFloatingAnimations();
    initConnectionLines();
    
    return () => {
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      }
    };
  }, []);

  const initParticleAnimation = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    particlesRef.current = [];
    const particleCount = 150;

    for (let i = 0; i < particleCount; i++) {
      particlesRef.current.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 3 + 1,
        speedX: (Math.random() - 0.5) * 0.8,
        speedY: (Math.random() - 0.5) * 0.8,
        color: Math.random() > 0.5 ? 'rgba(0, 198, 255, 0.6)' : 'rgba(41, 128, 185, 0.6)',
        alpha: Math.random() * 0.5 + 0.2,
        waveOffset: Math.random() * Math.PI * 2
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particlesRef.current.forEach((p, i) => {
        p.x += p.speedX;
        p.y += p.speedY + Math.sin(Date.now() * 0.001 + p.waveOffset) * 0.3;
        
        if (p.x > canvas.width) p.x = 0;
        if (p.x < 0) p.x = canvas.width;
        if (p.y > canvas.height) p.y = 0;
        if (p.y < 0) p.y = canvas.height;
        
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color.replace('0.6', p.alpha.toString());
        ctx.fill();
        
        for (let j = i + 1; j < particlesRef.current.length; j++) {
          const p2 = particlesRef.current[j];
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 120) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(0, 198, 255, ${0.15 * (1 - distance/120)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        }
      });
      
      requestAnimationFrame(animate);
    };
    
    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  };

  const initFloatingAnimations = () => {
    const floatElements = document.querySelectorAll('.floating-element');
    floatElements.forEach((el, index) => {
      el.style.animationDelay = `${index * 0.2}s`;
    });
  };

  const initConnectionLines = () => {
    const lines = document.querySelectorAll('.connection-line');
    lines.forEach((line, index) => {
      line.style.setProperty('--delay', `${index * 0.2}s`);
    });
  };

  const initScrollAnimations = () => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    document.querySelectorAll('[data-animate]').forEach(el => {
      observer.observe(el);
    });
  };

  const lifecycleServices = [
    {
      id: 1,
      title: "Post Go-Live Optimization",
      description: "Identifying and resolving business process bottlenecks for maximum efficiency.",
      icon: "⚙️"
    },
    {
      id: 2,
      title: "AMS (Application Management Services)",
      description: "24/7 support and maintenance for uninterrupted Workday operations.",
      icon: "🛡️"
    },
    {
      id: 3,
      title: "Test Automation",
      description: "Automated testing solutions ensuring flawless deployments and upgrades.",
      icon: "🤖"
    },
    {
      id: 4,
      title: "Strategic Reviews & Roadmaps",
      description: "Comprehensive strategic reviews and future-proof roadmaps.",
      icon: "🗺️"
    },
    {
      id: 5,
      title: "Post Go-Live Stabilization",
      description: "Ensuring smooth transition and system stability after implementation.",
      icon: "⚖️"
    }
  ];

  const values = [
    {
      id: 1,
      title: "Excellence",
      description: "Delivering solutions that exceed expectations and set new standards.",
      icon: "🏆"
    },
    {
      id: 2,
      title: "Innovation",
      description: "Pioneering cutting-edge solutions and forward-thinking approaches.",
      icon: "💡"
    },
    {
      id: 3,
      title: "Partnership",
      description: "Building lasting relationships based on trust and mutual success.",
      icon: "🤝"
    },
    {
      id: 4,
      title: "Integrity",
      description: "Upholding honesty, transparency, and ethical practices in all engagements.",
      icon: "⚖️"
    }
  ];

  return (
    <div className="about-page">
      <canvas ref={canvasRef} className="particle-canvas"></canvas>

      <section className="about-hero-section" ref={heroRef}>
        <div className="tech-background">
          <div className="tech-grid"></div>
          <div className="tech-glow"></div>
          <div className="data-flow">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="data-line" style={{ '--delay': `${i * 0.2}s` }}></div>
            ))}
          </div>
          
          <div className="floating-tech-elements">
            <div className="tech-element element-1">⚡</div>
            <div className="tech-element element-2">🔗</div>
            <div className="tech-element element-3">📊</div>
            <div className="tech-element element-4">🔒</div>
            <div className="tech-element element-5">🌐</div>
          </div>
        </div>

        <div className="hero-gradient-overlay"></div>

        <div className="about-hero-container">
          <div className="about-hero-content" data-animate="fade-up">
            <div className="about-breadcrumb">
              <span className="breadcrumb-item" onClick={() => navigate("/")}>
                <span className="breadcrumb-icon">🏠</span>
                <span className="breadcrumb-text">Home</span>
              </span>
              <span className="breadcrumb-separator">/</span>
              <span className="breadcrumb-active">
                <span className="breadcrumb-icon">📄</span>
                <span className="breadcrumb-text">About Us</span>
              </span>
            </div>
            
            
            
            <div className="hero-text-content" data-animate="fade-up" data-delay="0.2">
              <h2 className="hero-tagline">
                Your Trusted Partner for{" "}
                <span className="text-gradient">Customized Solutions</span>
              </h2>
              
              <div className="hero-description">
                <p className="desc-main">
                  <span className="highlight">Expert Support</span>, and{" "}
                  <span className="highlight">Seamless Integration</span> of Workday HCM and Financial Management.
                </p>
                
                <p className="desc-sub">
                  Enhance your experience with effortless engagement. Step into a bespoke partnership 
                  aligned with your goals and aspirations, where you remain in control, and we are here 
                  to support you whenever you need us.
                </p>
              </div>
            </div>
            
            <div className="hero-stats" data-animate="slide-up" data-delay="0.4">
              <div className="stat-item">
                <div className="stat-icon">🏢</div>
                <div className="stat-content">
                  <div className="stat-number">10+</div>
                  <div className="stat-label">Years Experience</div>
                </div>
                <div className="stat-glow"></div>
              </div>
              
              <div className="stat-item">
                <div className="stat-icon">✅</div>
                <div className="stat-content">
                  <div className="stat-number">50+</div>
                  <div className="stat-label">Projects</div>
                </div>
                <div className="stat-glow"></div>
              </div>
              
              <div className="stat-item">
                <div className="stat-icon">👥</div>
                <div className="stat-content">
                  <div className="stat-number">20+</div>
                  <div className="stat-label">Clients</div>
                </div>
                <div className="stat-glow"></div>
              </div>
              
              
            </div>
            
            <div className="hero-cta" data-animate="fade-up" data-delay="0.6">
              <button 
                className="btn-primary btn-glow"
                onClick={() => navigate("/contact")}
              >
                <span className="btn-icon">🚀</span>
                <span className="btn-text">Get Started Today</span>
                <span className="btn-arrow">→</span>
              </button>
              
              <button 
                className="btn-secondary btn-outline"
                onClick={() => navigate("/services")}
              >
                <span className="btn-icon">🔍</span>
                <span className="btn-text">Explore Services</span>
              </button>
            </div>
            
            <div className="scroll-indicator">
              <div className="mouse">
                <div className="wheel"></div>
              </div>
              <span className="scroll-text">Scroll to explore</span>
              <div className="scroll-arrow">↓</div>
            </div>
          </div>
          
          {/* NEW VISUAL DESIGN - Tech Architecture Network */}
          <div className="hero-visual-right" data-animate="slide-right">
            {/* Main Network Container */}
            <div className="tech-architecture">
              {/* Central Hub */}
              <div className="central-hub">
                <div className="hub-glow"></div>
                <div className="hub-icon">🏢</div>
                <div className="hub-text">JCRL CORNERSTONE</div>
              </div>
              
              {/* Satellite Nodes */}
              <div className="satellite-nodes">
                <div className="satellite-node node-1">
                  <div className="node-icon">💼</div>
                  <div className="node-label">Workday HCM</div>
                  <div className="node-glow"></div>
                </div>
                
                <div className="satellite-node node-2">
                  <div className="node-icon">💰</div>
                  <div className="node-label">Financial Mgmt</div>
                  <div className="node-glow"></div>
                </div>
                
                <div className="satellite-node node-3">
                  <div className="node-icon">⚙️</div>
                  <div className="node-label">Custom Solutions</div>
                  <div className="node-glow"></div>
                </div>
                
                <div className="satellite-node node-4">
                  <div className="node-icon">🛡️</div>
                  <div className="node-label">Expert Support</div>
                  <div className="node-glow"></div>
                </div>
                
                <div className="satellite-node node-5">
                  <div className="node-icon">🔗</div>
                  <div className="node-label">Seamless Integration</div>
                  <div className="node-glow"></div>
                </div>
                
                <div className="satellite-node node-6">
                  <div className="node-icon">🚀</div>
                  <div className="node-label">Innovation</div>
                  <div className="node-glow"></div>
                </div>
              </div>
              
              {/* Connection Lines */}
              <div className="connection-lines">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="connection-line" style={{ '--i': i }}></div>
                ))}
              </div>
              
              {/* Floating Data Points */}
              <div className="floating-data">
                {[...Array(20)].map((_, i) => (
                  <div 
                    key={i} 
                    className="data-point"
                    style={{
                      '--delay': `${i * 0.1}s`,
                      '--x': `${Math.random() * 100}%`,
                      '--y': `${Math.random() * 100}%`
                    }}
                  >
                    <div className="data-pulse"></div>
                  </div>
                ))}
              </div>
              
              {/* Orbiting Rings */}
              <div className="orbiting-rings">
                <div className="orbit-ring ring-1"></div>
                <div className="orbit-ring ring-2"></div>
                <div className="orbit-ring ring-3"></div>
              </div>
            </div>
            
            {/* Technology Stack Icons */}
            <div className="tech-stack">
              <div className="tech-icon">☁️</div>
              <div className="tech-icon">🤖</div>
              <div className="tech-icon">📱</div>
              <div className="tech-icon">💾</div>
              <div className="tech-icon">🔐</div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-intro-section">
        <div className="about-intro-container">
          <div className="intro-left" data-animate="slide-left">
            <div className="intro-badge">
              <span className="badge-text">WHO WE ARE</span>
            </div>
            <h2 className="intro-title">
              Leading Workday Advisory <span className="highlight">Globally</span>
            </h2>
            
            <div className="intro-content">
              <p>
                At <span className="highlight">JCRL CORNERSTONE TECHNOLOGIES</span>, we aim to excel as a global leader in 
                Workday advisory services. We set the standard for innovation, offering cutting-edge 
                solutions and forward-thinking guidance.
              </p>
              
              <p>
                Join us in shaping the future of Workday advisory and redefining industry standards 
                through our commitment to excellence and client success.
              </p>
              
              <div className="intro-stats">
                <div className="intro-stat">
                  <div className="stat-number">10+</div>
                  <div className="stat-label">Years Experience</div>
                </div>
                <div className="intro-stat">
                  <div className="stat-number">50+</div>
                  <div className="stat-label">Projects Completed</div>
                </div>
                <div className="intro-stat">
                  <div className="stat-number">50+</div>
                  <div className="stat-label">Experts Team</div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="intro-right" data-animate="slide-right">
            <div className="intro-visual">
              <div className="floating-circle circle-1"></div>
              <div className="floating-circle circle-2"></div>
              <div className="floating-circle circle-3"></div>
              
              <div className="visual-center">
                <div className="center-icon">🚀</div>
                <div className="center-text">Innovation & Excellence</div>
              </div>
              
              <div className="visual-orbits">
                <div className="orbit orbit-1">
                  <div className="orbit-dot">💡</div>
                  <div className="orbit-label">Strategy</div>
                </div>
                <div className="orbit orbit-2">
                  <div className="orbit-dot">⚡</div>
                  <div className="orbit-label">Technology</div>
                </div>
                <div className="orbit orbit-3">
                  <div className="orbit-dot">🤝</div>
                  <div className="orbit-label">Partnership</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      

      <section className="values-section">
        <div className="values-container">
          <div className="section-header" data-animate="fade-up">
            <div className="section-badge">
              <span className="badge-text">OUR VALUES</span>
            </div>
            <h2 className="section-title">
              What We <span className="highlight">Stand For</span>
            </h2>
            <p className="section-subtitle">
              The principles that guide our every decision and action
            </p>
          </div>
          
          <div className="values-grid">
            {values.map((value, index) => (
              <div 
                key={value.id} 
                className="value-card"
                data-animate="zoom-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="value-icon-wrapper">
                  <div className="value-icon">{value.icon}</div>
                </div>
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
              </div>
            ))}
          </div>
          
          <div className="values-content" data-animate="fade-up">
            <div className="values-text">
              <h3>Our Commitment to Excellence</h3>
              <p>
                At JCRL CORNERSTONE TECHNOLOGIES, our values are more than just words—they're 
                the foundation of our company culture and the driving force behind our success. 
                We believe in building lasting partnerships based on trust, delivering innovative 
                solutions that drive real business value, and maintaining the highest standards 
                of integrity in all our interactions.
              </p>
              
              <p>
                Our team of certified Workday experts is dedicated to helping you maximize your 
                investment, streamline operations, and achieve your strategic objectives through 
                comprehensive Workday solutions.
              </p>
            </div>
            
            <div className="values-visual" data-animate="slide-right">
              <div className="visual-card">
                <div className="card-header">
                  <div className="card-icon">🎯</div>
                  <h4>Our Mission</h4>
                </div>
                <p>
                  To empower organizations with expert Workday solutions that drive 
                  operational excellence and measurable business value.
                </p>
              </div>
              
              <div className="visual-card">
                <div className="card-header">
                  <div className="card-icon">👁️</div>
                  <h4>Our Vision</h4>
                </div>
                <p>
                  To be the global leader in Workday advisory and automation services, 
                  setting new standards for innovation and client success.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="global-section">
        <div className="global-container">
          <div className="section-header" data-animate="fade-up">
            <div className="section-badge">
              <span className="badge-text">GLOBAL PRESENCE</span>
            </div>
            <h2 className="section-title">
              Serving Clients <span className="highlight">Worldwide</span>
            </h2>
            <p className="section-subtitle">
              Delivering exceptional Workday solutions across industries and continents
            </p>
          </div>
          
          <div className="global-stats" data-animate="zoom-in">
           
            <div className="stat-item">
              <div className="stat-number">50+</div>
              <div className="stat-label">Projects</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">99%</div>
              <div className="stat-label">Satisfaction</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">24/7</div>
              <div className="stat-label">Support</div>
            </div>
          </div>
          
          <div className="global-content" data-animate="fade-up">
            <p>
              From North America to Europe, Asia to Australia, we've built a reputation for 
              delivering exceptional Workday solutions that transform businesses. Our global 
              footprint allows us to bring international best practices and local expertise 
              to every project.
            </p>
            
            <div className="industry-list">
              <div className="industry-item">
                <div className="industry-icon">🏢</div>
                <span>Financial Services</span>
              </div>
              <div className="industry-item">
                <div className="industry-icon">🏥</div>
                <span>Healthcare</span>
              </div>
              <div className="industry-item">
                <div className="industry-icon">🏭</div>
                <span>Manufacturing</span>
              </div>
              <div className="industry-item">
                <div className="industry-icon">🛒</div>
                <span>Retail</span>
              </div>
              <div className="industry-item">
                <div className="industry-icon">🎓</div>
                <span>Education</span>
              </div>
              <div className="industry-item">
                <div className="industry-icon">⚡</div>
                <span>Energy</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-cta-section">
        <div className="cta-background"></div>
        
        <div className="cta-container" data-animate="zoom-in">
          <div className="cta-icon">🚀</div>
          <h2 className="cta-title">
            Ready to Transform Your <span className="highlight">Workday Experience</span>?
          </h2>
          
          <p className="cta-description">
            Join the growing list of global organizations that trust JCRL CORNERSTONE 
            TECHNOLOGIES for their Workday journey. Let's build something exceptional together.
          </p>
          
          <div className="cta-buttons">
            <button 
              className="btn-cta-primary"
              onClick={() => navigate("/contact")}
            >
              Get in Touch
            </button>
            <button 
              className="btn-cta-secondary"
              onClick={() => navigate("/services")}
            >
              View Services
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;